/**
 * Freeze V2 — Cell Edit Hook
 *
 * Handles onCellValueChanged for the master fertilization grid. The live and
 * temp embryo tables commit their own edits inline (see LiveEmbryoGrid /
 * TempEmbryoGrid).
 */
import { useCallback } from 'react';

import type { CellValueChangedEvent } from 'ag-grid-community';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';

import { useUpdateFertilizationFieldMutation } from '../services/freezeApi';
import { FIELD_NAMES } from '../utils/constants';

const NO_EMBRYOS_PRODUCED_VALUE = 'No Embryos Produced';
const CONTAMINANT_PRESENT_VALUE = 'Contaminant Present';

export function useCellEdit() {
  const dispatch = useDispatch();
  const { mutateAsync: updateFert } = useUpdateFertilizationFieldMutation();

  const handleFertCellValueChanged = useCallback(
    async (event: CellValueChangedEvent) => {
      event.node.setSelected(true);
      const field = event.column.getColDef().field as string;
      let value = event.data[field];

      if (field === FIELD_NAMES.NO_EMBRYOS_PRODUCED) {
        value = value === true ? NO_EMBRYOS_PRODUCED_VALUE : '';
      }
      if (field === FIELD_NAMES.CONTAMINANT_PRESENT) {
        value = value === true ? CONTAMINANT_PRESENT_VALUE : '';
      }

      try {
        await updateFert({
          id: event.data.fertId,
          field,
          value: value?.toString() ?? null,
        });
      } catch (error) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Save Failed',
            detail: `Could not save ${field}: ${error}`,
            life: 3000,
          }),
        );
      }
    },
    [updateFert, dispatch],
  );

  return { handleFertCellValueChanged };
}
